import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Switch,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  FormErrorMessage,
  Badge,
  IconButton,
  Flex,
  Text
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import axios from '../api';

const CustomStrategySymbols = () => {
  const [symbols, setSymbols] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    symbol: '',
    option_type: '',
    is_active: true
  });
  const [formErrors, setFormErrors] = useState({});
  const toast = useToast();

  const fetchSymbols = async () => {
    try {
      const response = await axios.get('/custom-strategy-symbols');
      setSymbols(response.data);
    } catch (error) {
      toast({
        title: '获取数据失败',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchSymbols();
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!formData.symbol.trim()) {
      errors.symbol = '标的代码不能为空';
    }
    if (!formData.option_type) {
      errors.option_type = '请选择期权类型';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await axios.post('/custom-strategy-symbols', formData);
      toast({
        title: '添加成功',
        status: 'success',
        duration: 2000,
      });
      setIsModalOpen(false);
      setFormData({ symbol: '', option_type: '', is_active: true });
      fetchSymbols();
    } catch (error) {
      toast({
        title: '添加失败',
        description: error.response?.data?.detail || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这个标的吗？')) return;

    try {
      await axios.delete(`/custom-strategy-symbols/${id}`);
      toast({
        title: '删除成功',
        status: 'success',
        duration: 2000,
      });
      fetchSymbols();
    } catch (error) {
      toast({
        title: '删除失败',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleStatusChange = async (id, is_active) => {
    try {
      const symbol = symbols.find(s => s.id === id);
      await axios.put(`/custom-strategy-symbols/${id}`, {
        ...symbol,
        is_active: !is_active
      });
      fetchSymbols();
    } catch (error) {
      toast({
        title: '更新状态失败',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getOptionTypeBadge = (type) => {
    const colors = {
      'CALL': 'green',
      'PUT': 'red',
    };
    const text = {
      'CALL': '看涨',
      'PUT': '看跌',
    };
    return (
      <Badge colorScheme={colors[type]}>
        {text[type]}
      </Badge>
    );
  };

  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <Box fontSize="xl" fontWeight="bold">自定义策略标的列表</Box>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="blue"
          onClick={() => setIsModalOpen(true)}
        >
          添加标的
        </Button>
      </Flex>

      <Table variant="striped" size="sm" colorScheme="gray">
        <Thead>
          <Tr>
            <Th>标的代码</Th>
            <Th>期权类型</Th>
            <Th>状态</Th>
            <Th>操作</Th>
          </Tr>
        </Thead>
        <Tbody>
          {symbols.map((symbol) => (
            <Tr 
              key={symbol.id} 
              _hover={{ bg: "blue.100" }}
            >
              <Td py={2}>{symbol.symbol}</Td>
              <Td py={2}>{getOptionTypeBadge(symbol.option_type)}</Td>
              <Td py={2}>
                <Switch
                  size="sm"
                  isChecked={symbol.is_active}
                  onChange={() => handleStatusChange(symbol.id, symbol.is_active)}
                  colorScheme="green"
                />
              </Td>
              <Td py={2}>
                <Button
                    size="xs"
                    height="22px"
                    minW="60px"
                    variant="outline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(symbol.id);
                    }}
                    colorScheme="red"
                >
                    删除
                </Button>


              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>添加新标的</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired isInvalid={!!formErrors.symbol} mb={4}>
              <FormLabel>标的代码</FormLabel>
              <Input
                placeholder="输入标的代码"
                value={formData.symbol}
                onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
              />
              <FormErrorMessage>{formErrors.symbol}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!formErrors.option_type}>
              <FormLabel>期权类型</FormLabel>
              <Select
                placeholder="选择期权类型"
                value={formData.option_type}
                onChange={(e) => setFormData({ ...formData, option_type: e.target.value })}
              >
                <option value="CALL">看涨</option>
                <option value="PUT">看跌</option>
              </Select>
              <FormErrorMessage>{formErrors.option_type}</FormErrorMessage>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
              取消
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              添加
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CustomStrategySymbols;